import { supabase } from './client'

type UserType = 'patient' | 'provider'

function generateOtp(): string {
  return Math.floor(100000 + Math.random() * 900000).toString()
}

async function lookupUser(email: string, userType: UserType) {
  const { data, error } = await supabase.rpc('get_user_for_password_reset', {
    email_param: email,
    user_type_param: userType,
  })

  return {
    data: Array.isArray(data) ? (data[0] ?? null) : data,
    error,
  }
}

export async function requestEmailVerificationOTP(
  email: string,
  userType: UserType,
): Promise<{ success: boolean; error?: string }> {
  try {
    const cleanEmail = (email || '').trim().toLowerCase()
    const alternateUserType: UserType = userType === 'patient' ? 'provider' : 'patient'

    if (!cleanEmail) {
      return { success: false, error: 'Email address is required' }
    }

    const { data: user, error: userError } = await lookupUser(cleanEmail, userType)
    if (userError) {
      return { success: false, error: 'Unable to verify this account right now. Please try again.' }
    }

    if (!user) {
      const { data: alternateUser, error: alternateLookupError } = await lookupUser(cleanEmail, alternateUserType)
      if (alternateLookupError) {
        return { success: false, error: 'Unable to verify this account right now. Please try again.' }
      }

      if (alternateUser) {
        return {
          success: false,
          error: `This email is registered as a ${alternateUserType} account. Use the ${alternateUserType} sign-in page.`,
        }
      }

      return { success: false, error: 'No account found with this email address' }
    }

    const otpCode = generateOtp()
    const expiresAt = new Date()
    expiresAt.setMinutes(expiresAt.getMinutes() + 10)

    const { error: insertError } = await supabase
      .from('email_verification_codes')
      .insert({
        email: cleanEmail,
        code: otpCode,
        user_type: userType,
        expires_at: expiresAt.toISOString(),
      })

    if (insertError) {
      return { success: false, error: 'Failed to generate verification code. Please try again.' }
    }

    const { error: emailError } = await supabase.functions.invoke('send-email', {
      body: {
        email: cleanEmail,
        name: (user as { name?: string | null })?.name || 'User',
        emailType: 'verification-otp',
        data: { otpCode, userType },
      },
    })

    if (emailError) {
      return { success: false, error: 'Failed to send verification email. Please try again.' }
    }

    return { success: true }
  } catch (error: any) {
    return { success: false, error: error?.message || 'Failed to send verification code' }
  }
}

export async function verifyEmailVerificationOTP(
  email: string,
  code: string,
  userType: UserType,
): Promise<{ success: boolean; error?: string }> {
  try {
    const cleanEmail = (email || '').trim().toLowerCase()
    const cleanCode = (code || '').trim()

    const { data: otpRecord, error: fetchError } = await supabase
      .from('email_verification_codes')
      .select('*')
      .eq('email', cleanEmail)
      .eq('code', cleanCode)
      .eq('user_type', userType)
      .eq('used', false)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (fetchError || !otpRecord) {
      return { success: false, error: 'Invalid verification code. Please check and try again.' }
    }

    if (new Date(otpRecord.expires_at).getTime() <= Date.now()) {
      return { success: false, error: 'Verification code has expired. Please request a new code.' }
    }

    await supabase
      .from('email_verification_codes')
      .update({ used: true })
      .eq('id', otpRecord.id)

    const { data: user, error: userError } = await lookupUser(cleanEmail, userType)
    if (userError || !user) {
      return { success: false, error: 'Unable to find user for verification update' }
    }

    if (userType === 'patient') {
      const { error: updateError } = await supabase
        .from('patients')
        .update({ email_verified: true })
        .eq('auth_id', (user as { auth_id: string }).auth_id)

      if (updateError) throw updateError
    } else {
      const { error: updateError } = await supabase
        .from('providers')
        .update({ is_verified: true })
        .eq('auth_id', (user as { auth_id: string }).auth_id)

      if (updateError) throw updateError
    }

    return { success: true }
  } catch (error: any) {
    return { success: false, error: error?.message || 'Failed to verify code' }
  }
}